import React, { useState } from 'react';
import {
  Search,
  MapPin,
  ShieldCheck,
  ArrowRight,
  CheckCircle2,
  Sparkles,
  Users,
  CreditCard,
  AlertCircle,
  Scale,
  Wrench,
  Star,
  Zap,
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { SERVICE_CATEGORIES } from '../data/categories';
import { ALL_NIGERIAN_STATES } from '../data/nigerianLocations';
import { CategoryIcon } from '../components/common/CategoryIcon';
import { VerificationBadge } from '../components/common/VerificationBadge';
import { Profile, ServiceCategory } from '../types';
import { TelegramAssistantSection } from '../components/TelegramAssistantSection';
import { VerifiedCustomerTestimonials } from '../components/VerifiedCustomerTestimonials';

export const HomePage: React.FC = () => {
  const { navigateTo, profiles, currentUser, openAuthModal } = useApp();
  const [searchCategory, setSearchCategory] = useState('');
  const [searchLocation, setSearchLocation] = useState('Lagos');

  const verifiedPros = profiles.filter((p: Profile) => p.role === 'professional').slice(0, 4);

  const handleHeroSearch = (e: React.FormEvent) => {
    e.preventDefault();
    navigateTo('find_pro', { selectedCategory: searchCategory || undefined });
  };

  return (
    <div className="space-y-12 sm:space-y-16 lg:space-y-20 pb-12 sm:pb-16">
      {/* 1. HERO SECTION */}
      <section className="relative overflow-hidden bg-slate-900 text-white pt-8 sm:pt-16 pb-12 sm:pb-24 px-4 sm:px-6 lg:px-8 border-b border-slate-800">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#10b981_1px,transparent_1px)] [background-size:16px_16px] pointer-events-none" />

        <div className="relative max-w-5xl mx-auto text-center space-y-5 sm:space-y-6">
          <div className="inline-flex flex-wrap items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-3.5 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-[11px] sm:text-xs font-bold max-w-full">
            <span>🇳🇬 Built for Nigeria</span>
            <span className="hidden sm:inline text-slate-500">·</span>
            <span className="text-center">Connecting You with Verified Local Artisans & Technicians</span>
          </div>

          <h1 className="text-2xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-tight">
            Find Trusted Artisans <br className="hidden sm:inline" />
            <span className="text-emerald-400">for Any Repair or Job</span>
          </h1>

          <p className="text-xs sm:text-base text-slate-300 max-w-2xl mx-auto leading-relaxed font-normal px-1">
            Plumbing, electrical, auto mechanics, AC, generators, carpentry, and painting.
            Get itemized quotes in Naira from NIN-verified professionals in your LGA.
          </p>

          {/* Primary Action Buttons */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 max-w-lg mx-auto pt-2">
            <a
              href="/post-job"
              id="hero-post-job-cta"
              onClick={(e) => {
                e.preventDefault();
                navigateTo('post_job');
              }}
              className="w-full sm:w-auto px-6 py-3.5 rounded-2xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs sm:text-sm font-bold shadow-lg shadow-emerald-600/30 transition-all flex items-center justify-center gap-2 min-h-[48px] cursor-pointer"
            >
              <Zap className="w-4 h-4 shrink-0" />
              <span>Post a Job Free</span>
              <ArrowRight className="w-4 h-4 shrink-0" />
            </a>
            <a
              href="/become-a-professional"
              id="hero-become-pro-cta"
              onClick={(e) => {
                e.preventDefault();
                navigateTo('become_pro');
              }}
              className="w-full sm:w-auto px-6 py-3.5 rounded-2xl bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 text-xs sm:text-sm font-semibold transition-all flex items-center justify-center gap-2 min-h-[48px] cursor-pointer"
            >
              <Wrench className="w-4 h-4 text-emerald-400 shrink-0" />
              <span>Become a Professional</span>
            </a>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-2.5 sm:gap-4 pt-1 text-xs text-slate-400">
            <a
              href="/find-a-professional"
              onClick={(e) => {
                e.preventDefault();
                navigateTo('find_pro');
              }}
              className="hover:text-emerald-400 font-semibold underline underline-offset-4 cursor-pointer"
            >
              Browse Artisan Directory
            </a>
            <span>•</span>
            <a
              href="/how-it-works"
              onClick={(e) => {
                e.preventDefault();
                navigateTo('how_it_works');
              }}
              className="hover:text-emerald-400 font-semibold underline underline-offset-4 cursor-pointer"
            >
              How It Works
            </a>
            {!currentUser && (
              <>
                <span>•</span>
                <button
                  onClick={() => openAuthModal('login')}
                  className="hover:text-emerald-400 text-slate-300 font-bold underline underline-offset-4 cursor-pointer"
                >
                  Sign In to Account
                </button>
              </>
            )}
          </div>

          {/* Search Form */}
          <form
            onSubmit={handleHeroSearch}
            className="max-w-3xl mx-auto bg-white rounded-2xl p-2 sm:p-2.5 shadow-xl flex flex-col sm:flex-row items-stretch sm:items-center gap-2 text-slate-800 mt-6"
          >
            <div className="w-full sm:w-1/2 flex items-center gap-2 px-3 py-2 border-b sm:border-b-0 sm:border-r border-slate-200 min-h-[44px]">
              <Search className="w-4 h-4 text-slate-400 shrink-0" />
              <select
                id="hero-service-select"
                value={searchCategory}
                onChange={(e) => setSearchCategory(e.target.value)}
                className="w-full !bg-transparent text-xs sm:text-sm font-medium !text-slate-800 focus:outline-none min-h-[36px] border-none"
              >
                <option value="">Select Service (e.g. Auto Mechanic, Plumber)</option>
                {SERVICE_CATEGORIES.map((cat: ServiceCategory) => (
                  <option key={cat.slug} value={cat.slug}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="w-full sm:w-1/3 flex items-center gap-2 px-3 py-2 min-h-[44px]">
              <MapPin className="w-4 h-4 text-emerald-600 shrink-0" />
              <select
                id="hero-location-select"
                value={searchLocation}
                onChange={(e) => setSearchLocation(e.target.value)}
                className="w-full !bg-transparent text-xs sm:text-sm font-medium !text-slate-800 focus:outline-none min-h-[36px] border-none"
              >
                <option value="">All Nigeria (36 States + FCT)</option>
                {ALL_NIGERIAN_STATES.map((st) => (
                  <option key={st.name} value={st.name}>
                    {st.name}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="submit"
              id="hero-search-submit-btn"
              className="w-full sm:w-auto px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs sm:text-sm font-bold shadow-md transition-all shrink-0 flex items-center justify-center gap-2 min-h-[44px] cursor-pointer"
            >
              <span>Find Artisans</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>

          {/* Popular Trade Badges */}
          <div className="pt-2 flex flex-wrap items-center justify-center gap-1.5 sm:gap-2 text-xs text-slate-300">
            <span className="text-slate-400 text-[11px]">Popular Trades:</span>
            {['auto-mechanic', 'plumber', 'electrician', 'ac-technician', 'generator-technician', 'carpenter', 'painter'].map((slug) => {
              const cat = SERVICE_CATEGORIES.find((c: ServiceCategory) => c.slug === slug);
              if (!cat) return null;
              return (
                <a
                  key={slug}
                  href={`/services/${slug}`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigateTo('find_pro', { selectedCategory: slug });
                  }}
                  className="px-2.5 py-1 rounded-full bg-slate-800/80 hover:bg-slate-700 border border-slate-700 text-slate-200 text-[11px] font-medium transition-colors min-h-[32px] inline-flex items-center cursor-pointer"
                >
                  {cat.name}
                </a>
              );
            })}
          </div>
        </div>

        {/* Real Platform Pillars */}
        <div className="max-w-5xl mx-auto mt-10 sm:mt-16 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 text-center border-t border-slate-800/80 pt-6 sm:pt-8">
          <div className="p-2 space-y-1">
            <div className="text-emerald-400 font-bold text-xs sm:text-base flex items-center justify-center gap-1.5">
              <ShieldCheck className="w-4 h-4 shrink-0" />
              <span>NIN & Liveness Vetted</span>
            </div>
            <p className="text-[11px] sm:text-xs text-slate-400">Verified identity & location</p>
          </div>

          <div className="p-2 space-y-1">
            <div className="text-white font-bold text-xs sm:text-base flex items-center justify-center gap-1.5">
              <Scale className="w-4 h-4 text-emerald-400 shrink-0" />
              <span>Itemized Quotes</span>
            </div>
            <p className="text-[11px] sm:text-xs text-slate-400">Labor & parts separated in Naira</p>
          </div>

          <div className="p-2 space-y-1">
            <div className="text-white font-bold text-xs sm:text-base flex items-center justify-center gap-1.5">
              <MapPin className="w-4 h-4 text-emerald-400 shrink-0" />
              <span>LGA-Level Matching</span>
            </div>
            <p className="text-[11px] sm:text-xs text-slate-400">Tradespeople in your area</p>
          </div>

          <div className="p-2 space-y-1">
            <div className="text-emerald-400 font-bold text-xs sm:text-base flex items-center justify-center gap-1.5">
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              <span>₦0 Free To Post</span>
            </div>
            <p className="text-[11px] sm:text-xs text-slate-400">Zero fee to compare bids</p>
          </div>
        </div>
      </section>

      {/* 2. EXPLORE POPULAR SERVICE CATEGORIES (MOVED DIRECTLY BELOW HERO) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-6 sm:mb-8 gap-4">
          <div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-800 text-xs font-bold border border-emerald-200 mb-2">
              <Wrench className="w-3.5 h-3.5 text-emerald-600" />
              <span>Verified Technical Trades</span>
            </div>
            <h2 className="text-xl sm:text-3xl font-extrabold text-slate-900 dark:text-white">
              Explore Available Service Categories
            </h2>
            <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 mt-1">
              Select a trade category below to request service or browse certified artisans.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <a
              href="/find-a-professional"
              onClick={(e) => {
                e.preventDefault();
                navigateTo('find_pro');
              }}
              className="px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-semibold transition-colors cursor-pointer"
            >
              Browse Directory
            </a>
            <a
              href="/post-job"
              onClick={(e) => {
                e.preventDefault();
                navigateTo('post_job');
              }}
              className="px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold transition-colors inline-flex items-center gap-1.5 cursor-pointer"
            >
              <span>Post a Job Free</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>

        <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3.5 sm:gap-4">
          {SERVICE_CATEGORIES.map((cat: ServiceCategory) => (
            <a
              key={cat.slug}
              href={`/services/${cat.slug}`}
              onClick={(e) => {
                e.preventDefault();
                navigateTo('find_pro', { selectedCategory: cat.slug });
              }}
              className="bg-white text-slate-900 rounded-2xl p-4 sm:p-5 border border-slate-200 hover:border-emerald-500 hover:shadow-md transition-all cursor-pointer flex flex-col justify-between space-y-3 group"
            >
              <div className="space-y-2">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                  <CategoryIcon slug={cat.slug} className="w-5 h-5" />
                </div>
                <h3 className="font-bold text-xs sm:text-sm text-slate-900 group-hover:text-emerald-700 transition-colors">
                  {cat.name}
                </h3>
                <p className="text-[11px] sm:text-xs text-slate-600 line-clamp-2 leading-relaxed">
                  {cat.description}
                </p>
              </div>

              <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-[11px]">
                <span className="font-bold text-emerald-700">{cat.typicalPriceRange}</span>
                <span className="text-slate-400 group-hover:text-emerald-600 font-semibold flex items-center gap-0.5">
                  View <ArrowRight className="w-3 h-3" />
                </span>
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* 3. SMART AI JOB DIAGNOSTIC & ESTIMATE FEATURE BANNER */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gradient-to-br from-emerald-900 via-slate-900 to-slate-950 text-white rounded-3xl p-6 sm:p-10 border border-emerald-800/40 shadow-xl relative overflow-hidden">
          <div className="absolute right-0 top-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="relative max-w-3xl space-y-4">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-bold border border-emerald-500/30">
              <Sparkles className="w-3.5 h-3.5 text-emerald-400" />
              <span>Smart AI Artisan Matching & Price Calculator</span>
            </div>

            <h2 className="text-2xl sm:text-3xl font-extrabold text-white leading-tight">
              Not Sure What Trade or Parts You Need? Let Our AI Diagnose It
            </h2>

            <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
              Describe your repair in your own words (e.g. <em>"My car brakes are grinding"</em>, <em>"AC blowing warm air"</em>, or <em>"Water pipe leaking under sink"</em>).
              Our AI automatically identifies the correct artisan trade, provides itemized material recommendations, and estimates realistic Nigerian labor costs.
            </p>

            {/* Interactive Prompt Examples */}
            <div className="pt-1 flex flex-wrap gap-2 text-xs">
              {[
                { label: '🚗 Car Mechanic needed', cat: 'auto-mechanic' },
                { label: '🚰 Burst plumbing pipe', cat: 'plumber' },
                { label: '💡 Sockets sparking', cat: 'electrician' },
                { label: '❄️ AC not cooling', cat: 'ac-technician' },
                { label: '⚡ Inverter battery fault', cat: 'solar-technician' },
              ].map((item) => (
                <button
                  key={item.label}
                  onClick={() => navigateTo('post_job', { category: item.cat })}
                  className="px-3 py-1.5 rounded-xl bg-slate-800/80 hover:bg-emerald-600 hover:text-white border border-slate-700 text-slate-200 text-xs font-medium transition-all cursor-pointer"
                >
                  {item.label}
                </button>
              ))}
            </div>

            <div className="pt-2">
              <button
                onClick={() => navigateTo('post_job')}
                className="px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs sm:text-sm shadow-md transition-all inline-flex items-center gap-2 cursor-pointer"
              >
                <span>Try AI Job Diagnosis Free</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 4. HOW IT WORKS (STREAMLINED 4-STEP JOURNEY) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-10 space-y-2">
          <span className="text-xs font-bold text-emerald-700 bg-emerald-50 px-3 py-1 rounded-full border border-emerald-200">
            Transparent Workflow
          </span>
          <h2 className="text-2xl sm:text-4xl font-extrabold text-slate-900">
            How JobMatch NG Works
          </h2>
          <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
            From repair description to verified completion in four straightforward steps.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            {
              step: '1',
              title: 'Describe Your Job',
              desc: 'Tell us what needs fixing or installing in plain words. Get an instant AI diagnosis and price benchmark.',
            },
            {
              step: '2',
              title: 'Get Vetted Matches',
              desc: 'Verified artisans registered in your local government area receive your request and submit bids.',
            },
            {
              step: '3',
              title: 'Compare Itemized Quotes',
              desc: 'Review transparent breakdowns separating labor charges from materials in Naira. Check ratings and badges.',
            },
            {
              step: '4',
              title: 'Pay Safely via Escrow',
              desc: 'Inspect completed physical workmanship before payment is released via secure Paystack protection.',
            },
          ].map((item) => (
            <div
              key={item.step}
              className="bg-white rounded-3xl p-6 border border-slate-200 shadow-xs hover:shadow-md transition-shadow space-y-3 relative flex flex-col justify-between"
            >
              <div className="space-y-3">
                <div className="w-10 h-10 rounded-2xl bg-emerald-600 text-white font-extrabold flex items-center justify-center text-sm shadow-sm">
                  {item.step}
                </div>
                <h3 className="font-extrabold text-base text-slate-900">{item.title}</h3>
                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 5. VERIFIED ARTISANS PREVIEW */}
      {verifiedPros.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-8 gap-4">
            <div>
              <span className="text-xs font-bold text-emerald-700 uppercase tracking-wider block">
                Vetted Professionals
              </span>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-1">
                Meet Verified Artisans Ready in Your Area
              </h2>
              <p className="text-xs sm:text-sm text-slate-500 mt-1">
                Identity, address, and live facial scans verified on JobMatch NG.
              </p>
            </div>

            <button
              onClick={() => navigateTo('find_pro')}
              className="px-4 py-2.5 rounded-xl border border-slate-200 hover:bg-slate-100 text-slate-700 text-xs font-semibold transition-colors cursor-pointer self-start sm:self-auto"
            >
              View Full Directory ({profiles.filter(p => p.role === 'professional').length}+ Artisans)
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {verifiedPros.map((pro: Profile) => (
              <div
                key={pro.id}
                className="bg-white rounded-3xl p-5 border border-slate-200 shadow-xs hover:shadow-md transition-all flex flex-col justify-between space-y-4"
              >
                <div className="space-y-3">
                  <div className="flex items-start justify-between">
                    <img
                      src={pro.avatarUrl || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&auto=format&fit=crop&q=80'}
                      alt={pro.fullName}
                      className="w-14 h-14 rounded-2xl object-cover ring-2 ring-emerald-100"
                    />
                    <VerificationBadge status={pro.verificationStatus || 'verified'} size="sm" />
                  </div>

                  <div>
                    <h3 className="font-extrabold text-sm text-slate-900">{pro.fullName}</h3>
                    <p className="text-xs text-emerald-700 font-bold capitalize">
                      {pro.primaryCategory || 'Technical Artisan'}
                    </p>
                    <p className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1">
                      <MapPin className="w-3 h-3 text-slate-400 shrink-0" />
                      <span>{pro.lga || 'Ikeja'}, {pro.state || 'Lagos'}</span>
                    </p>
                  </div>

                  <div className="flex items-center gap-3 text-xs text-slate-600 bg-slate-50 p-2.5 rounded-xl">
                    <div className="flex items-center gap-1 text-amber-600 font-bold">
                      <Star className="w-3.5 h-3.5 fill-amber-500 text-amber-500" />
                      <span>{pro.rating || '5.0'}</span>
                    </div>
                    <span>•</span>
                    <span>{pro.yearsExperience || 4}+ Years Exp.</span>
                  </div>
                </div>

                <button
                  onClick={() =>
                    navigateTo('post_job', {
                      selectedCategory: pro.primaryCategory,
                      category: pro.primaryCategory,
                      targetProfessionalId: pro.id,
                      targetProfessionalName: pro.fullName,
                      targetProfessionalBusiness: pro.businessName || `${pro.fullName} Technical Services`,
                      targetProfessionalAvatar: pro.avatarUrl,
                    })
                  }
                  className="w-full py-2.5 px-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs shadow-xs transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <span>Request Quote</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 6. VERIFIED CUSTOMER TESTIMONIALS CAROUSEL */}
      <VerifiedCustomerTestimonials />

      {/* 7. DUAL VALUE PROPOSITION (CUSTOMERS VS ARTISANS) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* For Customers */}
          <div className="bg-white rounded-3xl p-6 sm:p-10 border border-slate-200 shadow-xs flex flex-col justify-between space-y-6">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-800 text-xs font-bold border border-emerald-200">
                <Users className="w-3.5 h-3.5 text-emerald-600" />
                <span>For Customers & Homeowners</span>
              </div>
              <h3 className="text-2xl font-extrabold text-slate-900">
                Hire Confidently with Upfront Price Quotes
              </h3>
              <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                Stop gambling with unvetted artisan contacts. Post any technical repair for free,
                receive multiple competitive quotes from verified local technicians, and inspect completed work
                before payment release.
              </p>
              <ul className="space-y-2.5 text-xs text-slate-700">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span>Free to post and compare quotes with zero obligations</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span>Photo & video uploads for accurate remote diagnosis</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span>Secure Paystack escrow protection holds funds safely</span>
                </li>
              </ul>
            </div>

            <div className="pt-2 flex items-center gap-3">
              <button
                onClick={() => navigateTo('post_job')}
                className="px-5 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs sm:text-sm font-bold shadow-sm transition-all flex items-center gap-2 cursor-pointer"
              >
                <span>Post a Job Free</span>
                <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => navigateTo('customer_guide')}
                className="px-4 py-3 rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-semibold transition-all cursor-pointer"
              >
                Customer Guide
              </button>
            </div>
          </div>

          {/* For Professionals */}
          <div className="bg-slate-900 text-white rounded-3xl p-6 sm:p-10 border border-slate-800 shadow-md flex flex-col justify-between space-y-6">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-bold border border-emerald-500/30">
                <Wrench className="w-3.5 h-3.5" />
                <span>For Skilled Artisans & Tradespeople</span>
              </div>
              <h3 className="text-2xl font-extrabold text-white">
                Grow Your Technical Trade Business in Nigeria
              </h3>
              <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
                Receive direct job notifications matching your trade in your local government area.
                Complete 4-step verification, enjoy ₦0 monthly subscription fees, submit itemized bids, and keep
                90% of your customer earnings via Paystack escrow protection.
              </p>
              <ul className="space-y-2.5 text-xs text-slate-300">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span>Real-time local job alerts in your registered LGA</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span>Verified artisan badge builds high customer credibility</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span>₦0 monthly subscription • Direct bank account payouts</span>
                </li>
              </ul>
            </div>

            <div className="pt-2 flex items-center gap-3">
              <button
                onClick={() => navigateTo('become_pro')}
                className="px-5 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs sm:text-sm font-bold shadow-sm transition-all flex items-center gap-2 cursor-pointer"
              >
                <span>Become a Professional</span>
                <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => navigateTo('how_it_works')}
                className="px-4 py-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 text-xs font-semibold transition-all cursor-pointer"
              >
                Learn More
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 7. VERIFICATION & TRUST PILLARS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-3xl p-6 sm:p-10 border border-slate-200 shadow-xs space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 pb-5">
            <div className="space-y-1">
              <span className="text-xs font-bold text-emerald-600 uppercase tracking-wider">
                Integrity & Accountability
              </span>
              <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900">
                Understanding Verification at JobMatch NG
              </h2>
            </div>

            <button
              onClick={() => navigateTo('verification')}
              className="text-xs font-bold text-emerald-700 hover:text-emerald-800 flex items-center gap-1 cursor-pointer"
            >
              <span>Read Full Verification Policy</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>

          <p className="text-xs sm:text-sm text-slate-600 leading-relaxed max-w-4xl">
            Verification helps JobMatch NG confirm information provided by professionals — including valid
            Nigerian identification (NIN Slip/Card), operational utility bills, service area declarations, and
            live camera facial matching.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 pt-1">
            <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200/80 space-y-1">
              <span className="text-xs font-bold text-slate-900 block">1. Government ID</span>
              <p className="text-xs text-slate-500">NIN Slip/Card, Passport or Driver's License.</p>
            </div>
            <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200/80 space-y-1">
              <span className="text-xs font-bold text-slate-900 block">2. Address Proof</span>
              <p className="text-xs text-slate-500">PHCN electricity bill or tenancy document.</p>
            </div>
            <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200/80 space-y-1">
              <span className="text-xs font-bold text-slate-900 block">3. Location Check</span>
              <p className="text-xs text-slate-500">Registered Nigerian State and LGA.</p>
            </div>
            <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200/80 space-y-1">
              <span className="text-xs font-bold text-slate-900 block">4. Live Facial Scan</span>
              <p className="text-xs text-slate-500">Biometric match against presented ID.</p>
            </div>
          </div>

          <div className="bg-amber-50/70 border border-amber-200 rounded-2xl p-4 text-xs text-amber-900 flex items-start gap-2.5">
            <AlertCircle className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
            <span>
              <strong>Safety Reminder:</strong> Verification confirms identity credentials. Customers should
              inspect completed work before final release of milestone funds.
            </span>
          </div>
        </div>
      </section>

      {/* 8. TELEGRAM AI ASSISTANT SECTION */}
      <TelegramAssistantSection />

      {/* 9. FAQ PREVIEW */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-8 space-y-2">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900">
            Frequently Asked Questions
          </h2>
          <p className="text-xs sm:text-sm text-slate-500">
            Quick answers about finding artisans and offering technical services.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-xs space-y-1.5">
            <h4 className="font-bold text-sm text-slate-900">How much does it cost to post a job?</h4>
            <p className="text-xs text-slate-600 leading-relaxed">
              Posting a job and comparing quotes is 100% free for customers. You only pay the professional
              for their physical work as agreed in their accepted quote.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-xs space-y-1.5">
            <h4 className="font-bold text-sm text-slate-900">How are professionals verified?</h4>
            <p className="text-xs text-slate-600 leading-relaxed">
              Professionals undergo a 4-step check: government ID (NIN Slip/Card), utility address confirmation,
              operational service area registration, and live biometric facial liveness.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-xs space-y-1.5">
            <h4 className="font-bold text-sm text-slate-900">Can an existing customer become a professional?</h4>
            <p className="text-xs text-slate-600 leading-relaxed">
              Yes! If you already have a customer account, you can register your artisan trade details to upgrade your profile and begin receiving local job requests in your LGA.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-xs space-y-1.5">
            <h4 className="font-bold text-sm text-slate-900">How do auto mechanics and other trades work?</h4>
            <p className="text-xs text-slate-600 leading-relaxed">
              Our AI accurately diagnoses automotive, plumbing, electrical, AC, and building trade requests, generating transparent Naira budget estimates and connecting you with qualified local artisans.
            </p>
          </div>
        </div>

        <div className="text-center pt-6">
          <button
            onClick={() => navigateTo('faq')}
            className="text-xs font-bold text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1 cursor-pointer"
          >
            <span>View All Frequently Asked Questions</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </section>

      {/* 10. FINAL CALL TO ACTION */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-slate-900 text-white rounded-3xl p-8 sm:p-14 text-center space-y-6 shadow-xl relative overflow-hidden">
          <h2 className="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
            Ready to Experience Dependable Service Delivery?
          </h2>
          <p className="text-xs sm:text-sm text-slate-300 max-w-xl mx-auto leading-relaxed">
            Join thousands of Nigerian homeowners and verified trade professionals connecting through
            JobMatch NG.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={() => openAuthModal('register')}
              className="w-full sm:w-auto px-8 py-4 rounded-2xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs sm:text-sm shadow-lg shadow-emerald-600/30 transition-all flex items-center justify-center gap-2 cursor-pointer"
            >
              <span>Get Started & Create Account</span>
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => openAuthModal('login')}
              className="w-full sm:w-auto px-8 py-4 rounded-2xl bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 font-semibold text-xs sm:text-sm transition-all flex items-center justify-center gap-2 cursor-pointer"
            >
              <span>Sign In to Account</span>
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};
